import React, { useRef, useEffect, useState } from "react";
import { select } from "d3-selection";
import { geoPath, geoNaturalEarth1 } from "d3-geo";
import { transition } from "d3-transition";
import { json } from "d3-fetch";

// List of country ISO alpha-3 codes based on the image
const highlightedCountries = [
  "BRA", // Brazil
  "PER", // Peru
  "BOL", // Bolivia
  "UKR", // Ukraine
  "MAR", // Morocco
  "SEN", // Senegal
  "GIN", // Guinea
  "CIV", // Ivory Coast
  "TGO", // Togo
  "NGA", // Nigeria
  "CAF", // Central African Republic
  "COD", // Democratic Republic of the Congo
  "AGO", // Angola
  "NAM", // Namibia
  "ZAF", // South Africa
  "MWI", // Malawi
  "KEN", // Kenya
  "SOM", // Somalia
  "ETH", // Ethiopia
  "KAZ", // Kazakhstan
  "UZB", // Uzbekistan
  "TUR", // Turkey
  "IRN", // Iran
  "PAK", // Pakistan
  "PHL", // Philippines
  "IND", // India
  "IDN", // Indonesia
  "NPL", // Nepal
  "MMR", // Myanmar
  "THA", // Thailand
  "VNM", // Vietnam
  "MYS", // Malaysia
];

const InteractiveMap = ({ currentCountry }) => {
  const svgRef = useRef(null);
  const [topo, setTopo] = useState(null); // Store GeoJSON

  useEffect(() => {
    // Updated to high-res 10m Natural Earth GeoJSON
    json(
      "https://raw.githubusercontent.com/nvkelso/natural-earth-vector/v5.0.1/geojson/ne_10m_admin_0_countries.geojson"
    )
      .then((data) => setTopo(data))
      .catch((error) => console.error("Error loading data:", error));
  }, []);

  useEffect(() => {
    if (!topo) return;

    const svg = select(svgRef.current);
    const width = 800;
    const height = 600;
    const projection = geoNaturalEarth1()
      .scale(230)
      .center([0, 20])
      .translate([width / 2, height / 2]);
    const path = geoPath().projection(projection);

    // Clear existing map if needed
    svg.selectAll("*").remove();

    const g = svg.append("g");

    const mouseOver = function (event, d) {
      select(".Country").transition().duration(200).style("opacity", 0.5);
      select(this)
        .transition()
        .duration(200)
        .style("opacity", 1)
        .style("stroke", "white");
    };

    const mouseLeave = function (event, d) {
      select(".Country").transition().duration(200).style("opacity", 0.8);
      select(this).transition().duration(200).style("stroke", "transparent");
    };

    // Draw paths - Updated property access to ADM0_A3
    g.selectAll("path")
      .data(topo.features)
      .enter()
      .append("path")
      .attr("d", path)
      .attr("fill", (d) =>
        highlightedCountries.includes(d.properties.ADM0_A3)
          ? "#b491d0"
          : "#d3d3d3"
      )
      .style("stroke", "transparent")
      .attr("class", "Country")
      .style("opacity", 0.8)
      .on("mouseover", mouseOver)
      .on("mouseleave", mouseLeave);
  }, [topo]);

  useEffect(() => {
    if (!topo) return;

    const svg = select(svgRef.current);
    const g = svg.select("g");
    const width = 800;
    const height = 600;
    const projection = geoNaturalEarth1()
      .scale(230)
      .center([0, 20])
      .translate([width / 2, height / 2]);
    const path = geoPath().projection(projection);

    const t = transition().duration(750);

    // Reset all paths to grey, regardless of highlighted status
    g.selectAll(".Country")
      .transition(t)
      .style("stroke", "transparent")
      .style("fill", "#d3d3d3");

    if (!currentCountry) {
      // Reset to full world
      g.transition(t).attr("transform", "");
      // Restore highlighted countries when not zoomed - Updated property access
      g.selectAll(".Country")
        .transition(t)
        .style("fill", (d) =>
          highlightedCountries.includes(d.properties.ADM0_A3)
            ? "#b491d0"
            : "#d3d3d3"
        );
      return;
    }

    // Updated property access to ADM0_A3
    const feature = topo.features.find(
      (d) => d.properties.ADM0_A3 === currentCountry
    );
    if (!feature) {
      console.warn(`Country ${currentCountry} not found`);
      return;
    }

    // Highlight only the zoomed country - Updated property access
    g.selectAll(".Country")
      .filter((d) => d.properties.ADM0_A3 === currentCountry)
      .transition(t)
      .style("stroke", "white")
      .style("stroke-width", "0.5px")
      .style("fill", "#b491d0"); // Darker purple color

    // Add white stroke to all other countries
    g.selectAll(".Country")
      .filter((d) => d.properties.ADM0_A3 !== currentCountry)
      .transition(t)
      .style("stroke", "white")
      .style("stroke-width", "0.3px")
      .style("fill", "#d3d3d3");

    const bounds = path.bounds(feature);
    const dx = bounds[1][0] - bounds[0][0];
    const dy = bounds[1][1] - bounds[0][1];
    const x = (bounds[0][0] + bounds[1][0]) / 2;
    const y = (bounds[0][1] + bounds[1][1]) / 2;
    const scale = Math.max(
      1,
      Math.min(8, 0.9 / Math.max(dx / width, dy / height))
    );
    // Adjust translate to shift the map more to the right (increase x offset)
    const translate = [width / 2 - scale * x + 200, height / 2 - scale * y];

    g.transition(t).attr(
      "transform",
      `translate(${translate}) scale(${scale})`
    );
  }, [currentCountry, topo]);

  return (
    <svg
      ref={svgRef}
      width="100%"
      height="auto"
      viewBox="0 0 800 600"
      preserveAspectRatio="xMidYMid meet"
    ></svg>
  );
};

export default InteractiveMap;